/**
 * `/admin/retention` — Task U11 (Tier-3 live-tenant readiness).
 *
 * `wp_retention_policies` has decided when this company's evidence is destroyed
 * since migration 022, and until this task it could only be changed with a database
 * client: no route, no screen. That is not a safety feature, it is an absence of
 * one — the people who most need to *raise* a window (a legal hold arriving, an
 * audit opening) are the least likely to have `psql`.
 *
 * `nav/menu.ts` gates it on `workforce.manage_retention` alone — the first item ever
 * on that code — and the route guard (`App.tsx`) refuses the whole screen before the
 * read below fires for a session that lacks it. The server is still the authority; a
 * `403` here renders through the ordinary `ApiErrorNote` path rather than being
 * swallowed.
 *
 * ── The draft carries only what the person actually touched ─────────────────
 *
 * `draft` is a map from data class to the fields that class's row has had changed,
 * exactly `GeoPolicyPanel`'s pattern: a class the administrator never touched is
 * absent from it, and the value shown for that class falls back to the loaded row.
 * Pressing save sends **only the classes in `draft`** — "changing only `cache` sends
 * one class and nothing else" is true by construction, not by a diff computed at
 * submit time. On eighteen rows that is not a nicety: a body that resent every class
 * would carry seventeen versions the administrator never looked at, and each one is
 * a chance to lose somebody else's concurrent edit.
 *
 * ── A mixed outcome renders per row, never as one toast ─────────────────────
 *
 * `PUT` answers `{ applied, rejected }` and both can be non-empty. The applied class
 * shows its new version beside it; the stale one shows an inline conflict note
 * beside **that class**, naming the version sent and the version stored. One toast
 * saying "some changes could not be saved" is the failure mode this whole unit
 * exists to avoid: on this screen, the class that silently did not save is the class
 * whose evidence keeps being deleted on the old schedule.
 *
 * The conflict note is keyed on the class, so a second save that succeeds clears it
 * for that class and leaves the others alone.
 *
 * ── The floor is shown, and it is not the enforcement ───────────────────────
 *
 * Each row prints its floor and where the floor comes from, and the number input's
 * `min` is set from it. That is a courtesy: the server refuses a below-floor value
 * whether or not this screen was involved, which is why the refusal is rendered as a
 * plain `400` here rather than being made impossible.
 */

import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';

import { useI18n } from '../../i18n';
import { formatInstant } from '../../i18n/datetime';
import type { MessageKey } from '../../i18n/messages';
import { useSession } from '../../auth/SessionProvider';
import {
  RETENTION_ADMIN_PATH,
  RETENTION_REASON_MIN_LENGTH,
  type RetentionDataClass,
  type RetentionPolicyChangeRequest,
  type RetentionPolicyClass,
  type RetentionPolicyList,
  type RetentionPolicyRejected,
  type RetentionPolicyUpdateRequest,
  type RetentionPolicyUpdateResult,
} from '../../api/retention';
import { useApiMutation } from '../../api/useApiMutation';
import { useApiQuery } from '../../api/useApiQuery';
import { ApiErrorNote } from '../../states/ApiErrorNote';
import { Badge } from '../../states/Badge';
import { Skeleton } from '../../states/Skeleton';
import { StatusNote } from '../../states/StatusNote';
import { ScreenHeading } from '../../shell/ScreenHeading';

/** What one row's draft may carry. Absent keys mean "leave this alone". */
interface ClassDraft {
  readonly retention_days?: number;
  readonly acknowledge_data_loss?: boolean;
  /**
   * Ticked after this class came back as a conflict, to re-arm its save.
   *
   * Fix round 1. After a partial accept the rows are re-read from the response, so a
   * refused class's `version` becomes the **winner's** version while the draft still
   * holds the administrator's number. Without this flag the next click would carry a
   * perfectly valid precondition and quietly overwrite the value that won — the
   * blind re-apply this screen exists to prevent, dressed up as a legitimate write.
   */
  readonly overwrite?: boolean;
}

type Draft = Readonly<Partial<Record<RetentionDataClass, ClassDraft>>>;

/** `retention.class.<data_class>` — one label per catalogue class. */
function classLabelKey(dataClass: RetentionDataClass): MessageKey {
  return `retention.class.${dataClass}` as MessageKey;
}

export function RetentionScreen(): ReactNode {
  const { t, locale } = useI18n();
  const { user } = useSession();
  const timezone = user?.timezone ?? 'Asia/Ho_Chi_Minh';

  const policies = useApiQuery<RetentionPolicyList>(RETENTION_ADMIN_PATH);
  const save = useApiMutation<RetentionPolicyUpdateRequest, RetentionPolicyUpdateResult>(
    RETENTION_ADMIN_PATH,
    'PUT',
  );

  const [draft, setDraft] = useState<Draft>({});
  const [reason, setReason] = useState('');

  /**
   * The last write the server actually **answered**, held in state.
   *
   * Fix round 2. It used to read `save.data`, which `useApiMutation` sets back to
   * `null` on the next error — so a single later `400` (a value below a floor, say)
   * discarded the conflict note, released the overwrite gate, and reverted every
   * version on screen to the original `GET`. No lost update followed, because the
   * CAS refuses those reverted versions, but the screen forgot a conflict it had
   * just shown, which is the same failure of memory in a friendlier costume.
   *
   * The transient "it saved" banner still keys on `save.data`, because that one
   * *should* disappear when the next attempt fails. What persists here is the state
   * of the world: versions, what landed, and what was refused.
   */
  const [lastAnswered, setLastAnswered] = useState<RetentionPolicyUpdateResult | null>(null);

  /**
   * The rows to render: the last answered write's `classes` when there has been one,
   * otherwise the read's.
   *
   * Not `policies.data` alone, and the difference is the point: after a partial
   * accept the read is stale for the classes that landed, and the response already
   * carries the whole catalogue re-read from the database. Preferring it means the
   * version shown after a save is the version the next save must send.
   */
  const rows: readonly RetentionPolicyClass[] =
    lastAnswered?.classes ?? policies.data?.classes ?? [];

  /** Conflicts from the last answered write, by class, so each renders beside its row. */
  const conflicts = useMemo(() => {
    const byClass = new Map<RetentionDataClass, RetentionPolicyRejected>();
    for (const row of lastAnswered?.rejected ?? []) byClass.set(row.data_class, row);
    return byClass;
  }, [lastAnswered]);

  /** Classes that landed on the last answered write, so each shows its new version. */
  const applied = useMemo(() => {
    const byClass = new Map<RetentionDataClass, number>();
    for (const row of lastAnswered?.applied ?? []) byClass.set(row.data_class, row.version);
    return byClass;
  }, [lastAnswered]);

  // Three things, on every answered write.
  //
  // The answer is **remembered**, so a later failure cannot erase what this one
  // established — see `lastAnswered` above.
  //
  // The classes that **landed** leave the draft. A refused class keeps the number
  // the administrator typed, beside the conflict note that says why it did not
  // save — clearing it would silently discard their intent and leave them staring
  // at a value they did not choose with no idea what they had asked for.
  //
  // The classes that were **refused** lose both of their acknowledgements. Each one
  // was minted against a specific stored value, and the refusal means that value has
  // moved: carrying `overwrite` forward would let one tick authorise overwriting a
  // second, different administrator's number the person never saw, and carrying
  // `acknowledge_data_loss` forward is the same mistake one level down — a draft
  // that was already a shortening becomes a DEEPER one when the winner raises the
  // window, and the tick was minted for the smaller loss. The overwrite gate would
  // still stop the write, so nothing lands unconfirmed either way; but an
  // acknowledgement that no longer describes what it acknowledges should not
  // survive, and the reasoning above applies to it verbatim.
  useEffect(() => {
    const result = save.data;
    if (result === null) return;
    setLastAnswered(result);
    const landed = new Set<string>(result.applied.map((row) => row.data_class));
    const refused = new Set<string>(result.rejected.map((row) => row.data_class));
    if (landed.size === 0 && refused.size === 0) return;
    setDraft((current) =>
      Object.fromEntries(
        Object.entries(current)
          .filter(([dataClass]) => !landed.has(dataClass))
          .map(([dataClass, change]) =>
            refused.has(dataClass)
              ? [dataClass, { ...change, overwrite: false, acknowledge_data_loss: false }]
              : [dataClass, change],
          ),
      ),
    );
  }, [save.data]);

  const touched = Object.keys(draft) as RetentionDataClass[];
  const reasonOk = reason.trim().length >= RETENTION_REASON_MIN_LENGTH;

  const daysFor = useCallback(
    (row: RetentionPolicyClass): number =>
      draft[row.data_class]?.retention_days ?? row.retention_days,
    [draft],
  );

  /** A class whose drafted number is below its stored one needs the acknowledgement. */
  const shorteningWithoutAck = rows.filter((row) => {
    const change = draft[row.data_class];
    if (change?.retention_days === undefined) return false;
    return change.retention_days < row.retention_days && change.acknowledge_data_loss !== true;
  });

  /**
   * A class whose drafted number the server would refuse outright.
   *
   * Fix round 1: the input's native `min`/`max` are the browser's business and are
   * bypassed by clearing the field (which yields `0`) or by anything that produces
   * `NaN` — which serialises to `null` and comes back as a zod type error rather
   * than the floor message the administrator needed. Checked here as well, so the
   * button is honest and the reason is on the row.
   */
  const outOfRange = rows.filter((row) => {
    const days = draft[row.data_class]?.retention_days;
    if (days === undefined) return false;
    return !Number.isInteger(days) || days < row.floor_days || days > row.max_days;
  });

  /**
   * A class that came back as a conflict and whose draft is still armed.
   *
   * The rows now carry the winner's version, so a second click would succeed — and
   * silently overwrite what won. It stays blocked until the administrator says
   * explicitly that overwriting is what they mean.
   */
  const conflictedWithoutOverwrite = rows.filter(
    (row) => conflicts.has(row.data_class) && draft[row.data_class]?.overwrite !== true,
  );

  const canSave =
    touched.length > 0 &&
    reasonOk &&
    shorteningWithoutAck.length === 0 &&
    outOfRange.length === 0 &&
    conflictedWithoutOverwrite.length === 0 &&
    save.status !== 'pending';

  const submit = (): void => {
    if (!canSave) return;
    const classes: RetentionPolicyChangeRequest[] = [];
    for (const row of rows) {
      const change = draft[row.data_class];
      if (change === undefined) continue;
      classes.push({
        data_class: row.data_class,
        // The precondition, taken from the row as it was last read — never derived
        // from `updated_at`, and never omitted.
        version: row.version,
        ...(change.retention_days === undefined ? {} : { retention_days: change.retention_days }),
        ...(change.acknowledge_data_loss === true ? { acknowledge_data_loss: true } : {}),
      });
    }
    if (classes.length === 0) return;
    void save.submit({ reason: reason.trim(), classes });
  };

  return (
    <div className="wp-stack">
      <ScreenHeading>{t('nav.retention')}</ScreenHeading>
      <p className="wp-small wp-muted" data-testid="u11-intro">
        {t('retention.intro')}
      </p>

      {policies.status === 'loading' ? (
        <Skeleton rows={6} variant="block" label={t('retention.subject')} testId="u11-skeleton" />
      ) : null}

      {policies.status === 'error' && policies.error !== null ? (
        <ApiErrorNote
          error={policies.error}
          onRetry={policies.reload}
          subject={t('retention.subject')}
          testId="u11-error"
        />
      ) : null}

      {rows.length === 0 ? null : (
        <form
          className="wp-stack"
          data-testid="u11-form"
          onSubmit={(event) => {
            event.preventDefault();
            submit();
          }}
        >
          <div className="wp-table-scroll">
            <table className="wp-table wp-retention-table" data-testid="u11-table">
              <caption className="wp-visually-hidden">{t('retention.table.caption')}</caption>
              <thead>
                <tr>
                  <th scope="col">{t('retention.column.dataClass')}</th>
                  <th scope="col">{t('retention.column.days')}</th>
                  <th scope="col">{t('retention.column.floor')}</th>
                  <th scope="col">{t('retention.column.updated')}</th>
                  <th scope="col">{t('retention.column.state')}</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => {
                  const conflict = conflicts.get(row.data_class);
                  const appliedVersion = applied.get(row.data_class);
                  const inputId = `u11-days-${row.data_class}`;
                  const shortening =
                    draft[row.data_class]?.retention_days !== undefined &&
                    daysFor(row) < row.retention_days;
                  return (
                    <tr key={row.data_class} data-testid={`u11-row-${row.data_class}`}>
                      <th scope="row">
                        <span>{t(classLabelKey(row.data_class))}</span>
                        <br />
                        <code className="wp-small wp-muted">{row.data_class}</code>
                      </th>
                      <td>
                        <label className="wp-visually-hidden" htmlFor={inputId}>
                          {t('retention.column.days')}
                        </label>
                        <input
                          className="wp-field__input"
                          id={inputId}
                          data-testid={`u11-days-${row.data_class}`}
                          type="number"
                          min={row.floor_days}
                          max={row.max_days}
                          value={daysFor(row)}
                          onChange={(event) => {
                            const raw = event.target.value.trim();
                            setDraft((current) => ({
                              ...current,
                              [row.data_class]: {
                                ...current[row.data_class],
                                // An empty field is "no change to this number", not
                                // zero. `Number('')` is `0` and `Number('x')` is
                                // `NaN`, and both used to reach the draft — one as a
                                // silent below-floor value, the other as a `null` on
                                // the wire.
                                ...(raw === '' || !/^[0-9]+$/.test(raw)
                                  ? {}
                                  : { retention_days: Number(raw) }),
                              },
                            }));
                          }}
                        />
                        <p
                          className="wp-small wp-muted"
                          data-testid={`u11-version-${row.data_class}`}
                        >
                          {appliedVersion === undefined
                            ? t('retention.version', { version: String(row.version) })
                            : t('retention.applied', { version: String(appliedVersion) })}
                        </p>
                        {shortening ? (
                          <label className="wp-check">
                            <input
                              type="checkbox"
                              data-testid={`u11-ack-${row.data_class}`}
                              checked={draft[row.data_class]?.acknowledge_data_loss === true}
                              onChange={(event) => {
                                const { checked } = event.target;
                                setDraft((current) => ({
                                  ...current,
                                  [row.data_class]: {
                                    ...current[row.data_class],
                                    acknowledge_data_loss: checked,
                                  },
                                }));
                              }}
                            />
                            <span className="wp-small">{t('retention.acknowledge')}</span>
                          </label>
                        ) : null}
                      </td>
                      <td>
                        <p className="wp-small" data-testid={`u11-floor-${row.data_class}`}>
                          {row.floor_source === 'record_keeping'
                            ? t('retention.floor.recordKeeping', { days: String(row.floor_days) })
                            : t('retention.floor.bounds', { days: String(row.floor_days) })}
                        </p>
                        <p className="wp-small wp-muted">
                          {t('retention.floor.range', {
                            min: String(row.min_days),
                            max: String(row.max_days),
                          })}
                        </p>
                      </td>
                      <td className="wp-small wp-muted">
                        {formatInstant(row.updated_at, locale, timezone)}
                      </td>
                      <td>
                        {row.awaiting_migration ? (
                          // `info`, not a warning: a class with no table yet is a
                          // declared absence, and colouring it as a problem would tell
                          // an administrator to go fix something that is not broken.
                          <Badge
                            severity="info"
                            icon="info"
                            label={t('retention.awaitingMigration')}
                            title={t('retention.awaitingMigration.hint')}
                            testId={`u11-awaiting-${row.data_class}`}
                          />
                        ) : (
                          <Badge
                            severity={row.enabled ? 'low' : 'info'}
                            icon={row.enabled ? 'circle-check' : 'pause'}
                            label={row.enabled ? t('retention.enabled') : t('retention.disabled')}
                            testId={`u11-state-${row.data_class}`}
                          />
                        )}
                        {conflict === undefined ? null : (
                          <StatusNote
                            severity="high"
                            role="alert"
                            title={t('retention.conflict.title')}
                            testId={`u11-conflict-${row.data_class}`}
                          >
                            {/*
                            The STORED number, not just the two version integers.
                            Fix round 1: the note used to carry only versions, so the
                            value the administrator would be overwriting was never on
                            screen — and the field above still shows their own
                            rejected draft. `row` comes from the response's refreshed
                            `classes`, so `row.retention_days` is what won.
                          */}
                            {t('retention.conflict.body', {
                              expected: String(conflict.expected_version),
                              actual: String(conflict.actual_version),
                              stored: String(row.retention_days),
                              yours: String(daysFor(row)),
                            })}
                            <label className="wp-check">
                              <input
                                type="checkbox"
                                data-testid={`u11-overwrite-${row.data_class}`}
                                checked={draft[row.data_class]?.overwrite === true}
                                onChange={(event) => {
                                  const { checked } = event.target;
                                  setDraft((current) => ({
                                    ...current,
                                    [row.data_class]: {
                                      ...current[row.data_class],
                                      overwrite: checked,
                                    },
                                  }));
                                }}
                              />
                              <span className="wp-small">
                                {t('retention.conflict.overwrite', {
                                  stored: String(row.retention_days),
                                })}
                              </span>
                            </label>
                          </StatusNote>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="wp-field">
            <label className="wp-field__label" htmlFor="u11-reason">
              {t('retention.reason')}
            </label>
            <input
              className="wp-field__input"
              id="u11-reason"
              data-testid="u11-reason"
              type="text"
              value={reason}
              onChange={(event) => {
                setReason(event.target.value);
              }}
            />
            <p className="wp-small wp-muted">
              {t('retention.reason.hint', { min: String(RETENTION_REASON_MIN_LENGTH) })}
            </p>
          </div>

          {shorteningWithoutAck.map((row) => (
            <StatusNote
              key={row.data_class}
              severity="high"
              title={t('retention.acknowledge.required', {
                from: String(row.retention_days),
                to: String(daysFor(row)),
              })}
              testId={`u11-ack-required-${row.data_class}`}
            />
          ))}

          {outOfRange.map((row) => (
            <StatusNote
              key={row.data_class}
              severity="high"
              title={t('retention.range.required', {
                dataClass: t(classLabelKey(row.data_class)),
                floor: String(row.floor_days),
                max: String(row.max_days),
              })}
              testId={`u11-range-required-${row.data_class}`}
            />
          ))}

          <div className="wp-formbar">
            <span className="wp-small wp-muted" data-testid="u11-dirty">
              {t('retention.dirty', { count: String(touched.length) })}
            </span>
            <button
              type="submit"
              className="wp-button wp-button--primary"
              data-testid="u11-save"
              disabled={!canSave}
            >
              {save.status === 'pending' ? t('retention.saving') : t('retention.save')}
            </button>
          </div>

          {save.error === null ? null : (
            <ApiErrorNote
              error={save.error}
              subject={t('retention.subject')}
              testId="u11-save-error"
            />
          )}

          {save.data === null || save.data.applied.length === 0 ? null : (
            <StatusNote
              severity="low"
              title={t('retention.saveOk', { count: String(save.data.applied.length) })}
              testId="u11-save-ok"
            />
          )}
        </form>
      )}
    </div>
  );
}
